/**
 * Small formatting helpers — pure functions for display strings.
 *
 * Kept app-local (not promoted to `@sill/ui`) until `apps/marketing` lands
 * and the duplication question is concrete (per ADR-0003 atom-promotion
 * rationale).
 */

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
  hour12: false,
});

// ADR-0160 — cache one formatter per non-UTC display zone. `timeZoneName:
// 'short'` appends the zone abbreviation (e.g. "EDT") so a converted time is
// never ambiguous about which zone it's in.
const zonedFormatterCache = new Map<string, Intl.DateTimeFormat>();
function zonedFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = zonedFormatterCache.get(timeZone);
  if (f === undefined) {
    f = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone,
      timeZoneName: 'short',
    });
    zonedFormatterCache.set(timeZone, f);
  }
  return f;
}

/**
 * Render an ISO-8601 UTC instant for interactive display (ADR-0160).
 *
 * `timeZone` defaults to `'UTC'`, in which case the output is byte-identical
 * to the pre-ADR-0160 form (`Jul 01, 2026, 19:42 UTC`). When the merchant has
 * a display-zone preference set (account default or per-site override,
 * resolved via `useDisplayTimeZone()`), the instant is rendered in that zone
 * with the short zone abbreviation (`Jul 01, 2026, 15:42 EDT`).
 *
 * This is DISPLAY-ONLY. The signed audit envelope, exports, and the security
 * activity trail (`formatUtcDateTime`) stay UTC — never route those through a
 * non-UTC `timeZone`.
 */
export function formatIsoDate(iso: string, timeZone = 'UTC'): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  if (timeZone === 'UTC') {
    return `${dateFormatter.format(date)} UTC`;
  }
  try {
    return zonedFormatter(timeZone).format(date);
  } catch {
    // Unknown zone (shouldn't happen — the preference is validated on write)
    // → fall back to the unambiguous UTC rendering rather than throw.
    return `${dateFormatter.format(date)} UTC`;
  }
}

/**
 * Calendar-date formatter (ADR-0067). Renders an ISO-8601 calendar date
 * `YYYY-MM-DD` (no timezone, no time component) into the dashboard's
 * standard short-date display form — e.g. `1990-05-12` → `May 12, 1990`.
 *
 * The wire is calendar-only by design (a birthday is not a UTC instant);
 * we force `timeZone: 'UTC'` on the formatter so the rendered day is
 * stable across browser locales — without this pin a `1990-05-12`
 * string parsed in a `-PT-` browser would render as `May 11, 1990`.
 *
 * Returns the raw input on a non-calendar string so the panel degrades
 * gracefully on a future drift.
 */
const calendarDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

export function formatIsoCalendarDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return iso;
  }
  // `new Date('1990-05-12')` parses as UTC midnight per ECMA-262 — the
  // formatter's UTC timezone pin then renders the calendar day stably.
  const date = new Date(`${iso}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return calendarDateFormatter.format(date);
}

export function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

export function formatCurrency(amount_cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount_cents / 100);
}

/**
 * Format an integer minor-unit amount (e.g. cents) + ISO 4217 currency
 * code into a buyer-facing string. Mirrors the rail settlement
 * evidence shape (`*_minor` integers, `currency: 'USD'`) so the
 * MandateDetail settlement panel renders the same value the rail
 * charged.
 *
 * Returns `'—'` for non-finite / non-integer / negative inputs OR a
 * currency code Intl can't resolve — the panel prefers an em-dash over
 * `NaN` / `Invalid Date` shapes on a malformed JSONB row.
 */
export function formatMinor(minor: number, currency = 'USD'): string {
  if (!Number.isFinite(minor) || !Number.isInteger(minor) || minor < 0) {
    return '—';
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(minor / 100);
  } catch {
    // Intl throws RangeError on an unknown currency code; degrade to a
    // bare number rather than crash the panel.
    return (minor / 100).toFixed(2);
  }
}

/**
 * Capitalize a person/account display name for read-only rendering,
 * regardless of how the user stored it — a name saved as "rich" should
 * read as "Rich" in the greeting, roster, etc. Only words that are
 * entirely lower-case are touched; any word already carrying an
 * upper-case letter is left verbatim, so intentional casing survives
 * ("VBOUT", "iPhone", "McKinsey", "d'Angelo"). Per-word, whitespace-
 * delimited.
 *
 * Display-only — never run this over an editable input's value (it would
 * fight the user's typing) or over externally-sourced names we render
 * faithfully (buyer / shipping). Mirrors `toDisplayCase` in the API's
 * team-invite email template; kept duplicated rather than promoted to a
 * shared package because the two live in different build targets and the
 * helper is trivial.
 */
export function formatDisplayName(name: string): string {
  return name.replace(/\S+/g, (word) =>
    /[A-Z]/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1),
  );
}

export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) {
    return iso;
  }
  const seconds = Math.max(0, Math.round((now - ms) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

/** Geo shape returned by the API (ADR-0150) — city, subdivision/state, country. */
export interface GeoLoc {
  city: string | null;
  region?: string | null;
  country: string | null;
}

/**
 * "City, ST, Country" — the ONE location label used across every surface
 * (activity trail + active sessions). Skips missing parts, so a country-only
 * IP (e.g. a VPN/proxy) renders just the country.
 */
export function formatLocation(loc: GeoLoc | null | undefined): string {
  if (loc == null) return '';
  return [loc.city, loc.region, loc.country]
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .join(', ');
}

/**
 * "2026-07-01 19:42 UTC" — the ONE timestamp format for audit/security
 * surfaces (active sessions + activity trail). Always UTC + unambiguous;
 * never browser-locale (`toLocaleString`), which drifts per viewer.
 */
export function formatUtcDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number, w = 2): string => n.toString().padStart(w, '0');
  return (
    `${p(d.getUTCFullYear(), 4)}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}` +
    ` ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`
  );
}
